<template>
  <div class="voting-age-chart" :class="className" v-inview="{ callback: onInview }">
    <h4 class="voting-age-chart__caption">20-39各年齡層投票率</h4>
    <div ref="figure" class="voting-age-chart__figure"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import VOTING_AGES_DATA from '@/data/voting-ages.json'

// const SVG_CONFIG = {
//   viewBox: '0 0 320 240',
//   preserveAspectRatio: 'xMidYMid meet',
//   width: 320,
//   height: 240
// }

export default {
  name: 'PopulationChart',
  data () {
    return {
      isInview: false,
      data: VOTING_AGES_DATA,
      chartWidth: 0,
      chartHeight: 0,
      svg: null,
      svgChart: null,
      xDomain: [],
      yDomain: [],
      xExtent: [],
      yExtent: [],
      xScale: null,
      yScale: null,
      xAxis: null,
      yAxis: null,
      yGridAxis: null,
      percentageFormatter: d3.format('.2%'),
      svgSize: {}
    }
  },
  computed: {
    className () {
      return {
        'voting-age-chart--inview': this.isInview
      }
    },
    chartData () {
      return this.data.detail.map(row => ({ ...row }))
    },
    chartAgeData () {
      return this.chartData.map(row => row.age)
    },
    chartProportionData () {
      return this.chartData.map(row => row.proportion)
    },
    axisInfo () {
      const { width } = this.svgSize
      const info = {
        xTicks: 10,
        yTicks: 8,
        titleFontSize: 12,
        tickFontSize: 12,
        tickAngle: -25,
        tickAnchor: 'end',
        gridColor: '#D3D3D3',
        padding: [ 10, 35, 50, 55 ]
      }
      if (width >= 500) {
        info.titleFontSize = 16
        info.tickFontSize = 16
        info.tickAngle = 0
        info.tickAnchor = 'middle'
        info.padding = [ 10, 35, 70, 75 ]
      }
      if (width >= 760) {
        info.xTicks = 20
        info.yTicks = 16
      }
      return { ...info }
    },
    lineInfo () {
      const { width } = this.svgSize
      const info = {
        stroke: '#dba19c',
        strokeWidth: 1.5
      }
      if (width >= 500) {
        info.strokeWidth = 3
      }
      return { ...info }
    },
    plotInfo () {
      const { width } = this.svgSize
      const info = {
        normal: {
          radius: 3,
          fill: '#ffffff',
          stroke: '#dba19c',
          strokeWidth: 2
        },
        lowest: {
          radius: 3,
          fill: '#c96f5c',
          stroke: '#dba19c',
          strokeWidth: 2
        },
        highest: {
          radius: 5,
          fill: '#ffffff',
          stroke: '#c96f5c',
          strokeWidth: 3
        }
      }
      if (width >= 500) {
        info.normal.radius = 4
        info.normal.strokeWidth = 3
        info.lowest.radius = 4
        info.lowest.strokeWidth = 3
        info.highest.radius = 7
        info.highest.strokeWidth = 5
      }
      return { ...info }
    },
    waterLineInfo () {
      const { width } = this.svgSize
      const info = {
        fill: '#1CA088',
        stroke: '#229983',
        strokeWidth: 1.5,
        fontSize: 12,
        dy: 18
      }
      if (width >= 500) {
        info.strokeWidth = 2.5
        info.fontSize = 16
        info.dy = 25
      }
      return { ...info }
    },
    highestPlotLabelInfo () {
      const { width } = this.svgSize
      const info = {
        fill: '#c96f5c',
        fontSize: 12,
        offsetX: 15,
        offsetY: -5
      }
      if (width >= 500) {
        info.fontSize = 16
        info.offsetX = 15
        info.offsetY = -5
      }
      return { ...info }
    },
    lowestPlotLabelInfo () {
      const { width } = this.svgSize
      const info = {
        fill: '#c96f5c',
        fontSize: 12,
        offsetX: -10,
        offsetY: 18
      }
      if (width >= 500) {
        info.fontSize = 16
        info.offsetX = -15
        info.offsetY = 25
      }
      return { ...info }
    }
  },
  methods: {
    onInview () {
      this.isInview = true
      this.$bus.$on('resize', this.resize)
      this.resize()
    },
    resize () {
      this.$nextTick(() => {
        if (!this.$refs.figure) {
          return
        }
        const bounds = this.$refs.figure.getBoundingClientRect()
        this.svgSize = {
          width: Math.round(bounds.width),
          height: Math.round(bounds.height)
        }
        this.draw()
      })
    },
    draw () {
      const { width, height } = this.svgSize
      const { xTicks, yTicks, titleFontSize, tickFontSize, tickAngle, tickAnchor, gridColor, padding } = this.axisInfo
      // update chart size
      this.chartWidth = width - padding[1] - padding[3]
      this.chartHeight = height - padding[0] - padding[2]
      this.svgChart
        .attr('transform', `translate(${padding[3]}, ${padding[0]})`)
        .transition()
        .style('opacity', 1)
      // domains
      this.xDomain = [ 19, 40 ]
      this.yDomain = [ 0.68, 0.51 ]
      this.xExtent = d3.extent(this.chartAgeData)
      this.yExtent = d3.extent(this.chartProportionData)
      // x-axis
      this.xScale
        .range([0, this.chartWidth])
        .domain(this.xDomain)
      this.xAxis
        .ticks(xTicks)
      this.svgChart
        .select('.x-axis')
        .attr('transform', `translate(0, ${this.chartHeight})`)
        .call(this.xAxis)
        .selectAll('text')
        .classed('.axis-tick', true)
        .attr('transform', `translate(0, 0) rotate(${tickAngle})`)
        .style('text-anchor', tickAnchor)
        .style('font-size', `${tickFontSize}px`)

      // y-axis
      this.yScale
        .range([0, this.chartHeight])
        .domain(this.yDomain)
      this.yAxis
        .ticks(yTicks)
        .tickFormat(d => Math.round(d * 100))
      this.svgChart
        .select('.y-axis')
        .call(this.yAxis)
        .selectAll('text')
        .style('font-size', `${tickFontSize}px`)

      // y-grid-axis
      this.yGridAxis
        .ticks(yTicks)
        .tickSize(-this.chartWidth)
        .tickFormat('')
      this.svgChart
        .select('.y-grid-axis')
        .call(this.yGridAxis)
        .selectAll('line')
        .attr('stroke', gridColor)

      // render chart content
      this.svgChart
        .selectAll('.scatter-line')
        .data([ this.chartData ])
        .join(this.drawLineEnter, this.drawLineUpdate, this.drawLineExit)
      this.svgChart
        .selectAll('.scatter-plot')
        .data(this.chartData)
        .join(this.drawPlotEnter, this.drawPlotUpdate, this.drawPlotExit)
      this.svgChart
        .selectAll('.water-line')
        .data([ this.data.total ])
        .join(this.drawWaterLineEnter, this.drawWaterLineUpdate, this.drawWaterLineExit)

      // update rwd position
      this.svgChart
        .select('.x-axis-title')
        .attr('x', this.chartWidth)
        .attr('y', this.chartHeight + padding[2] * 0.8)
        .style('font-size', `${titleFontSize}px`)

      this.svgChart
        .select('.y-axis-title')
        .attr('x', -10)
        .attr('y', -padding[3] * 0.7)
        .style('font-size', `${titleFontSize}px`)
    },
    drawLineEnter (enter) {
      const { stroke, strokeWidth } = this.lineInfo
      let line = enter
        .append('path')
      line
        .classed('scatter-line', true)
        .attr('fill', 'none')
        .attr('stroke', stroke)
        .attr('stroke-width', strokeWidth)
        .attr('d', d3.line()
          .x(d => this.xScale(d.age))
          .y(d => this.yScale(d.proportion))
        )
      let path = line.node()
      if (path) {
        let len = path.getTotalLength()
        line
          .attr('stroke-dasharray', len)
          .attr('stroke-dashoffset', len)
          .transition()
          .duration(3000)
          .ease(d3.easeLinear)
          .attr('stroke-dashoffset', 0)
      }
    },
    plotInfoHandler (d) {
      let value = d.proportion
      let plotType = 'normal'
      if (value === this.yExtent[0]) {
        plotType = 'lowest'
      } else if (value === this.yExtent[1]) {
        plotType = 'highest'
      }
      return this.plotInfo[plotType]
    },
    drawLineUpdate (update) {
      const { stroke, strokeWidth } = this.lineInfo
      update
        .attr('fill', 'none')
        .attr('stroke', stroke)
        .attr('stroke-width', strokeWidth)
        .attr('stroke-dasharray', 'none')
        .attr('stroke-dashoffset', 0)
        .attr('d', d3.line()
          .x(d => this.xScale(d.age))
          .y(d => this.yScale(d.proportion))
        )
    },
    drawLineExit (exit) {
      exit.remove()
    },
    drawPlotEnter (enter) {
      const {
        fill: highestFill,
        fontSize: highestFontSize,
        offsetX: highestOffsetX,
        offsetY: highestOffsetY
      } = this.highestPlotLabelInfo
      const {
        fill: lowestFill,
        fontSize: lowestFontSize,
        offsetX: lowestOffsetX,
        offsetY: lowestOffsetY
      } = this.lowestPlotLabelInfo
      const totlaTime = 3000
      const stepUnitTime = totlaTime / this.chartData.length
      // enter
      let plot = enter
        .append('g')
        .classed('scatter-plot', true)
      plot
        .append('circle')
        .attr('cx', d => this.xScale(d.age))
        .attr('cy', d => this.yScale(d.proportion))
        .attr('r', 0)
        .attr('fill', d => this.plotInfoHandler(d).fill)
        .attr('stroke', d => this.plotInfoHandler(d).stroke)
        .attr('stroke-width', d => this.plotInfoHandler(d).strokeWidth)
        .style('opacity', 0)
        .transition()
        .duration(300)
        .delay((d, index) => index * stepUnitTime)
        .attr('r', d => this.plotInfoHandler(d).radius)
        .style('opacity', 1)
      // the highest plot
      let highestLabel = plot
        .filter(d => d.proportion === this.yExtent[1])
        .append('g')
        .classed('plot-label', true)
        .attr('transform', d => `translate(${this.xScale(d.age) + highestOffsetX}, ${this.yScale(d.proportion) + highestOffsetY + 10})`)
        .style('opacity', 0)
      highestLabel
        .append('text')
        .classed('age-text', true)
        .text(d => `${d.age}歲`)
        .attr('fill', highestFill)
        .style('font-size', `${highestFontSize}px`)
        .style('font-weight', 600)
      highestLabel
        .append('text')
        .classed('proportion-text', true)
        .text(d => this.percentageFormatter(d.proportion))
        .attr('dy', '1.4em')
        .attr('fill', highestFill)
        .style('font-size', `${highestFontSize}px`)
        .style('font-weight', 600)
      highestLabel
        .transition()
        .delay(300)
        .duration(400)
        .attr('transform', d => `translate(${this.xScale(d.age) + highestOffsetX}, ${this.yScale(d.proportion) + highestOffsetY})`)
        .style('opacity', 1)
      // the lowest plot
      let lowestLabel = plot
        .filter(d => d.proportion === this.yExtent[0])
        .append('g')
        .classed('plot-label', true)
        .attr('transform', d => `translate(${this.xScale(d.age) + lowestOffsetX}, ${this.yScale(d.proportion) + lowestOffsetY + 10})`)
        .style('opacity', 0)
      lowestLabel
        .append('text')
        .classed('age-text', true)
        .text(d => `${d.age}歲`)
        .attr('fill', lowestFill)
        .style('font-size', `${lowestFontSize}px`)
        .style('font-weight', 600)
      lowestLabel
        .append('text')
        .classed('proportion-text', true)
        .text(d => this.percentageFormatter(d.proportion))
        .attr('dy', '1.4em')
        .attr('fill', lowestFill)
        .style('font-size', `${lowestFontSize}px`)
        .style('font-weight', 600)
      lowestLabel
        .transition()
        .delay(4 * stepUnitTime + 300)
        .duration(400)
        .attr('transform', d => `translate(${this.xScale(d.age) + lowestOffsetX}, ${this.yScale(d.proportion) + lowestOffsetY})`)
        .style('opacity', 1)
    },
    drawPlotUpdate (update) {
      const {
        fill: highestFill,
        fontSize: highestFontSize,
        offsetX: highestOffsetX,
        offsetY: highestOffsetY
      } = this.highestPlotLabelInfo
      const {
        fill: lowestFill,
        fontSize: lowestFontSize,
        offsetX: lowestOffsetX,
        offsetY: lowestOffsetY
      } = this.lowestPlotLabelInfo
      update
        .select('circle')
        .attr('cx', d => this.xScale(d.age))
        .attr('cy', d => this.yScale(d.proportion))
        .attr('r', d => this.plotInfoHandler(d).radius)
        .attr('fill', d => this.plotInfoHandler(d).fill)
        .attr('stroke', d => this.plotInfoHandler(d).stroke)
        .attr('stroke-width', d => this.plotInfoHandler(d).strokeWidth)
        .style('opacity', 1)
      // the highest plot
      let highestLabel = update
        .filter(d => d.proportion === this.yExtent[1])
        .select('.plot-label')
        .attr('transform', d => `translate(${this.xScale(d.age) + highestOffsetX}, ${this.yScale(d.proportion) + highestOffsetY})`)
        .style('opacity', 1)
      highestLabel
        .select('.age-text')
        .text(d => `${d.age}歲`)
        .attr('fill', highestFill)
        .style('font-size', `${highestFontSize}px`)
        .style('font-weight', 600)
      highestLabel
        .select('.proportion-text')
        .text(d => this.percentageFormatter(d.proportion))
        .attr('dy', '1.4em')
        .attr('fill', highestFill)
        .style('font-size', `${highestFontSize}px`)
        .style('font-weight', 600)
      // the lowest plot
      let lowestLabel = update
        .filter(d => d.proportion === this.yExtent[0])
        .select('.plot-label')
        .attr('transform', d => `translate(${this.xScale(d.age) + lowestOffsetX}, ${this.yScale(d.proportion) + lowestOffsetY})`)
        .style('opacity', 1)
      lowestLabel
        .select('.age-text')
        .text(d => `${d.age}歲`)
        .attr('fill', lowestFill)
        .style('font-size', `${lowestFontSize}px`)
        .style('font-weight', 600)
      lowestLabel
        .select('.proportion-text')
        .text(d => this.percentageFormatter(d.proportion))
        .attr('dy', '1.4em')
        .attr('fill', lowestFill)
        .style('font-size', `${lowestFontSize}px`)
        .style('font-weight', 600)
    },
    drawPlotExit (exit) {
      exit.remove()
    },
    drawWaterLineEnter (enter) {
      const { fill, stroke, strokeWidth, fontSize, dy } = this.waterLineInfo
      let waterLine = enter
        .append('g')
        .classed('water-line', true)
      waterLine
        .append('line')
        .attr('x1', this.xScale(this.xDomain[0]))
        .attr('x2', this.xScale(this.xDomain[1]))
        .attr('y1', d => this.yScale(this.yDomain[1]))
        .attr('y2', d => this.yScale(this.yDomain[1]))
        .attr('stroke', stroke)
        .attr('stroke-width', strokeWidth)
        .attr('stroke-dasharray', '6')
        .style('opacity', 0)
        .transition()
        .duration(1000)
        .delay(3000)
        .attr('y1', d => this.yScale(d))
        .attr('y2', d => this.yScale(d))
        .style('opacity', 1)
      waterLine
        .append('text')
        .text(d => `全國投票率 ${this.percentageFormatter(d)}`)
        .attr('fill', fill)
        .attr('text-anchor', 'end')
        .attr('x', d => this.xScale(this.xDomain[1]) - 50)
        .attr('y', d => this.yScale(d))
        .attr('dy', dy)
        .style('font-size', `${fontSize}px`)
        .style('font-weight', 600)
        .style('opacity', 0)
        .transition()
        .duration(500)
        .delay(4000)
        .attr('x', d => this.xScale(this.xDomain[1]))
        .style('opacity', 1)
    },
    drawWaterLineUpdate (update) {
      const { fill, stroke, strokeWidth, fontSize, dy } = this.waterLineInfo
      update
        .select('line')
        .attr('x1', this.xScale(this.xDomain[0]))
        .attr('y1', d => this.yScale(d))
        .attr('x2', this.xScale(this.xDomain[1]))
        .attr('y2', d => this.yScale(d))
        .attr('stroke', stroke)
        .attr('stroke-width', strokeWidth)
        .attr('stroke-dasharray', '4')
      update
        .select('text')
        .text(d => `全國投票率 ${this.percentageFormatter(d)}`)
        .attr('fill', fill)
        .attr('text-anchor', 'end')
        .attr('x', d => this.xScale(this.xDomain[1]))
        .attr('y', d => this.yScale(d))
        .attr('dy', dy)
        .style('font-size', `${fontSize}px`)
        .style('font-weight', 600)
    },
    drawWaterLineExit (exit) {
      exit.remove()
    }
  },
  mounted () {
    const svg = d3
      .select(this.$refs.figure)
      .append('svg')
      // .attr('viewBox', SVG_CONFIG.viewBox)
      // .attr('preserveAspectRatio', SVG_CONFIG.preserveAspectRatio)
      // .attr('width', SVG_CONFIG.width)
      // .attr('height', SVG_CONFIG.height)
    const svgChart = svg
      .append('g')
      .style('opacity', 0)
    const xScale = d3.scaleLinear()
    const yScale = d3.scaleLinear()
    const xAxis = d3.axisBottom(xScale)
    const yAxis = d3.axisLeft(yScale)
    const yGridAxis = d3.axisLeft(yScale)
    // add y-grid-axis
    svgChart
      .append('g')
      .classed('y-grid-axis', true)
    // add x-axis
    svgChart
      .append('g')
      .classed('x-axis', true)
    // add y-axis
    svgChart
      .append('g')
      .classed('y-axis', true)
    // add x-axis title
    svgChart
      .append('text')
      .classed('axis-title', true)
      .classed('x-axis-title', true)
      .text('年齡（歲）')
      .attr('text-anchor', 'end')
      .attr('fill', '#949494')
    // add y-axis title
    svgChart
      .append('text')
      .classed('axis-title', true)
      .classed('y-axis-title', true)
      .text('投票率（百分比%）')
      .attr('text-anchor', 'end')
      .attr('fill', '#949494')
      .attr('transform', 'rotate(-90)')
      // .style('writing-mode', 'tb')
    this.svg = svg
    this.svgChart = svgChart
    this.xScale = xScale
    this.yScale = yScale
    this.xAxis = xAxis
    this.yAxis = yAxis
    this.yGridAxis = yGridAxis
  }
}
</script>

<style lang="scss">
.voting-age-chart {
  $comp: &;

  &__caption {
    font-size: 1.125em;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.1em;
    opacity: 0;
    @include rwd($RWD_TABLET_SMALL) {
      font-size: 1.25em;
    }
    @include rwd($RWD_TABLET) {
      font-size: 1.5em;
    }
  }

  &__figure {
    position: relative;
    max-height: 500px;
    margin: 0 auto;
    @include use-fixed-ratio(7, 5);
    @include rwd($RWD_TABLET_SMALL) {
      margin-top: 10px;
    }
    @include rwd($RWD_TABLET) {
      margin-top: 10px;
    }

    svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      text {
        font-family: "Montserrat", "Noto Sans TC", sans-serif;
      }
    }
  }

  &#{$comp}--inview {
    #{$comp}__caption {
      opacity: 1;
      transition: opacity 0.4s;
    }
  }
}
</style>
